// Currency conversion helpers.
// Rows with geography === 'India' are treated as INR native.
// Rows with any other geography (notably 'Offshore') are treated as USD
// and converted using the reporting period's USD→INR rate.

export const isOffshoreRow = (row) => {
  if (!row) return false;
  const g = (row.geography || '').toLowerCase();
  return g && g !== 'india' && g !== 'n/a' && g !== 'na' && g !== '—';
};

// `usdInr` may be a Number (single legacy rate applied to both cost & MV)
// OR an object { cost, mv } from the new dual-rate model.
const _rate = (usdInr, kind) => {
  if (usdInr && typeof usdInr === 'object') return Number(usdInr[kind]) || 0;
  return Number(usdInr) || 0;
};

// Returns the INR value of a row's `cost`. For offshore rows:
// - if `costInr` override is set (>0), it wins
// - else multiply USD cost by the cost-side FX rate
export const convertedCost = (row, usdInr) => {
  if (!row) return 0;
  const v = Number(row.cost) || 0;
  if (!isOffshoreRow(row)) return v;
  if (row.costInr != null && row.costInr !== '' && Number(row.costInr) > 0) {
    return Number(row.costInr);
  }
  const r = _rate(usdInr, 'cost');
  return r ? v * r : v;
};

export const convertedMarketValue = (row, usdInr) => {
  if (!row) return 0;
  const v = Number(row.marketValue) || 0;
  if (!isOffshoreRow(row)) return v;
  if (row.marketValueInr != null && row.marketValueInr !== '' && Number(row.marketValueInr) > 0) {
    return Number(row.marketValueInr);
  }
  const r = _rate(usdInr, 'mv');
  return r ? v * r : v;
};

export const convertedPnl = (row, usdInr) =>
  convertedMarketValue(row, usdInr) - convertedCost(row, usdInr);

// True if at least one row in the given snapshot uses offshore geography
export const snapshotHasOffshore = (snapshotId, holdings) => {
  const bySrc = holdings[snapshotId] || {};
  for (const rows of Object.values(bySrc)) {
    for (const r of rows) if (isOffshoreRow(r)) return true;
  }
  return false;
};

// Manual-only FX entry. No external API calls; the user enters each USD→INR rate
// from FxPromptDialog (and FxRateStrip in Data Entry).

// Resolve the FX rate that should apply to a given (snapshot, source) pair.
// Lookup order: source-specific → snapshot `_default` → first per-source entry
// in that snapshot → 0. Returns `{ cost, mv }` ready for convertedCost / MV.
export const fxForSource = (fxRates, snapId, srcId) => {
  const bySnap = (fxRates && fxRates[snapId]) || null;
  if (!bySnap) return { cost: 0, mv: 0 };
  const pick =
    (srcId && bySnap[srcId]) ||
    bySnap._default ||
    Object.entries(bySnap).find(([k, v]) => k !== '_default' && v && typeof v === 'object')?.[1] ||
    null;
  if (!pick) return { cost: 0, mv: 0 };
  return {
    cost: Number(pick.usdInrCost || pick.usdInr || 0),
    mv: Number(pick.usdInrMv || pick.usdInr || 0),
  };
};

// Iterate `holdings[snapId]` ({ srcId: rows[] }) applying per-source FX.
// Returns flat list of { row, srcId, cost, mv } with costs/values already in INR.
export const convertedRows = (bySrc, fxRates, snapId) => {
  const out = [];
  Object.entries(bySrc || {}).forEach(([srcId, rows]) => {
    const fx = fxForSource(fxRates, snapId, srcId);
    (rows || []).forEach((r) => {
      out.push({
        row: r,
        srcId,
        cost: convertedCost(r, fx),
        mv: convertedMarketValue(r, fx),
      });
    });
  });
  return out;
};
